/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import { logger } from './logger';

// Performance metrics tracking
interface PerformanceMetrics {
  pageLoadTime?: number;
  timeToFirstByte?: number;
  firstContentfulPaint?: number;
  largestContentfulPaint?: number;
  firstInputDelay?: number;
  cumulativeLayoutShift?: number;
  timeToInteractive?: number;
}

// Business metrics tracking
interface BusinessMetrics {
  event: string;
  properties?: Record<string, any>;
  userId?: string;
  organizationId?: string;
  timestamp?: Date;
}

// Error context for better debugging
interface ErrorContext {
  userId?: string;
  organizationId?: string;
  floorPlanId?: string;
  action?: string;
  metadata?: Record<string, any>;
}

class MonitoringService {
  private initialized = false;
  private metricsBuffer: BusinessMetrics[] = [];
  private flushInterval: NodeJS.Timeout | null = null;
  private activeUsers = new Set<string>();
  private userActivityInterval: NodeJS.Timeout | null = null;
  private errorCount = 0;
  private requestCount = 0;
  private totalResponseTime = 0;

  /**
   * Initialize monitoring with Sentry and performance tracking
   */
  initialize(options?: {
    dsn?: string;
    environment?: string;
    release?: string;
    sampleRate?: number;
    tracesSampleRate?: number;
  }) {
    if (this.initialized) {
      logger.warn('Monitoring already initialized');
      return;
    }

    const isDevelopment = import.meta.env.DEV;
    const environment = options?.environment || (isDevelopment ? 'development' : 'production');

    const sentryDsn = options?.dsn || import.meta.env.VITE_SENTRY_DSN;

    // Log initialization attempt (development only)
    if (import.meta.env.DEV) {
      console.log('🚀 Monitoring initialization:', {
        hasDsn: !!sentryDsn,
        dsnLength: sentryDsn?.length || 0,
        environment,
        isDevelopment,
      });
    }

    // Initialize Sentry
    if (sentryDsn) {
      if (import.meta.env.DEV) {
        console.log('📊 Initializing Sentry with DSN');
      }
      Sentry.init({
        dsn: sentryDsn,
        environment,
        release: options?.release || import.meta.env.VITE_APP_VERSION,
        sampleRate: options?.sampleRate ?? 1.0, // VF-43: capture ALL error events by default
        tracesSampleRate: options?.tracesSampleRate || (isDevelopment ? 1.0 : 0.1),
        integrations: [Sentry.browserTracingIntegration()],
        beforeSend(event, hint) {
          // Filter out certain errors in development
          if (isDevelopment && hint.originalException) {
            const error = hint.originalException as Error;
            if (error.message?.includes('ResizeObserver')) {
              return null; // Ignore ResizeObserver errors
            }
          }
          return event;
        },
      });

      this.initialized = true;
      if (import.meta.env.DEV) {
        console.log('✅ Sentry initialized successfully', { environment });
        logger.info('Monitoring initialized', { environment });
      }
    } else {
      if (import.meta.env.DEV) {
        console.warn('⚠️ Sentry DSN not configured, monitoring disabled');
        logger.warn('Sentry DSN not configured, monitoring disabled');
      }
    }

    // Start metrics flush interval (every 30 seconds)
    this.flushInterval = setInterval(() => {
      this.flushMetrics();
    }, 30000);

    // Clean up inactive users every 5 minutes
    this.userActivityInterval = setInterval(
      () => {
        this.cleanupInactiveUsers();
      },
      5 * 60 * 1000,
    );

    // Track initial page load performance
    this.trackPageLoadPerformance();

    // Track current user as active
    this.trackUserActivity();
  }

  /**
   * Track page load performance metrics
   */
  private trackPageLoadPerformance() {
    if (typeof window === 'undefined') return;

    // Use Performance Observer API for Core Web Vitals
    if ('PerformanceObserver' in window) {
      // Largest Contentful Paint
      try {
        const lcpObserver = new PerformanceObserver(list => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.trackPerformance({
            largestContentfulPaint: lastEntry.startTime,
          });
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (e) {
        logger.debug('LCP observer not supported');
      }

      // First Input Delay
      try {
        const fidObserver = new PerformanceObserver(list => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            this.trackPerformance({
              firstInputDelay: entry.processingStart - entry.startTime,
            });
          });
        });
        fidObserver.observe({ type: 'first-input', buffered: true });
      } catch (e) {
        logger.debug('FID observer not supported');
      }

      // Cumulative Layout Shift
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver(list => {
          for (const entry of list.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              clsValue += (entry as any).value;
              this.trackPerformance({
                cumulativeLayoutShift: clsValue,
              });
            }
          }
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });
      } catch (e) {
        logger.debug('CLS observer not supported');
      }
    }

    // Traditional performance metrics
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = window.performance.timing;
        const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
        const timeToFirstByte = perfData.responseStart - perfData.navigationStart;

        this.trackPerformance({
          pageLoadTime,
          timeToFirstByte,
        });
      }, 0);
    });
  }

  /**
   * Track custom business event
   */
  trackEvent(event: string, properties?: Record<string, any>) {
    const metric: BusinessMetrics = {
      event,
      properties,
      timestamp: new Date(),
    };

    // Add user context if available
    const user = Sentry.getCurrentScope().getUser();
    if (user) {
      metric.userId = user.id;
    }

    this.metricsBuffer.push(metric);

    // Also send to Sentry as breadcrumb
    Sentry.addBreadcrumb({
      category: 'business',
      message: event,
      level: 'info',
      data: properties,
    });

    logger.debug('Business event tracked', { event, properties });
  }

  /**
   * Check if monitoring is enabled
   */
  get isEnabled(): boolean {
    return this.initialized;
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metrics: PerformanceMetrics) {
    // Track as custom event since getTransaction is not available in current SDK
    // In production with proper Sentry setup, this would track actual performance metrics
    if (this.initialized && import.meta.env.PROD) {
      this.trackEvent('performance_metrics', metrics as any);
    }

    // Log metrics in development for debugging
    if (import.meta.env.DEV) {
      logger.debug('Performance metrics', metrics);
    }
  }

  /**
   * Report an error with context
   */
  reportError(
    error: Error,
    context?: ErrorContext & { level?: string; extra?: any; tags?: Record<string, string> },
  ) {
    // Add context to Sentry
    if (context) {
      Sentry.withScope(scope => {
        if (context.userId) scope.setUser({ id: context.userId });
        if (context.organizationId) scope.setTag('organization', context.organizationId);
        if (context.floorPlanId) scope.setTag('floorPlan', context.floorPlanId);
        if (context.action) scope.setTag('action', context.action);

        // Add any additional tags
        if (context.tags) {
          Object.entries(context.tags).forEach(([key, value]) => {
            if (value) scope.setTag(key, value);
          });
        }

        // Add metadata and extra context
        if (context.metadata) {
          Object.entries(context.metadata).forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
        }
        if (context.extra) {
          scope.setExtra('additional_context', context.extra);
        }

        // Set severity level
        if (context.level) {
          scope.setLevel(context.level as Sentry.SeverityLevel);
        }

        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }

    // Also log locally
    logger.error('Error reported to monitoring', error, context);
  }

  /**
   * Set user context for tracking
   */
  setUser(user: { id: string; email?: string; username?: string }) {
    Sentry.setUser(user);
    this.activeUsers.add(user.id);
    logger.debug('User context set', { userId: user.id });
  }

  /**
   * Clear user context (on logout)
   */
  clearUser() {
    const user = Sentry.getCurrentScope().getUser();
    if (user?.id) {
      this.activeUsers.delete(user.id);
    }
    Sentry.setUser(null);
    logger.debug('User context cleared');
  }

  /**
   * Track user activity
   */
  trackUserActivity(userId?: string) {
    const id = userId || Sentry.getCurrentScope().getUser()?.id || 'anonymous';
    this.activeUsers.add(id);

    // Store activity timestamp
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(`user_activity_${id}`, Date.now().toString());
    }
  }

  /**
   * Clean up inactive users
   */
  private cleanupInactiveUsers() {
    const now = Date.now();
    const inactivityThreshold = 5 * 60 * 1000; // 5 minutes

    this.activeUsers.forEach(userId => {
      const lastActivity = sessionStorage.getItem(`user_activity_${userId}`);
      if (lastActivity) {
        const lastActivityTime = parseInt(lastActivity, 10);
        if (now - lastActivityTime > inactivityThreshold) {
          this.activeUsers.delete(userId);
          sessionStorage.removeItem(`user_activity_${userId}`);
        }
      }
    });
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(feature: string, metadata?: Record<string, any>) {
    this.trackEvent(`feature_used_${feature}`, metadata);
  }

  /**
   * Track API performance
   */
  trackApiCall(endpoint: string, duration: number, status: number, error?: Error) {
    const isError = status >= 400;

    // Update metrics
    this.requestCount++;
    this.totalResponseTime += duration;
    if (isError) {
      this.errorCount++;
    }

    this.trackEvent('api_call', {
      endpoint,
      duration,
      status,
      success: !isError,
      error: error?.message,
    });

    if (isError && error) {
      this.reportError(error, {
        action: `API Call: ${endpoint}`,
        metadata: { status, duration },
      });
    }
  }

  /**
   * Start a performance transaction
   */
  startTransaction(name: string, op: string = 'navigation') {
    return Sentry.startSpan({ name, op }, () => {
      // Transaction logic here
    });
  }

  /**
   * Flush buffered metrics
   */
  private async flushMetrics() {
    if (this.metricsBuffer.length === 0) return;

    const metrics = [...this.metricsBuffer];
    this.metricsBuffer = [];

    try {
      // In production, send to analytics endpoint
      if (!import.meta.env.DEV && import.meta.env.VITE_ANALYTICS_ENDPOINT) {
        await fetch(import.meta.env.VITE_ANALYTICS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ metrics }),
        });
      }

      logger.debug(`Flushed ${metrics.length} metrics`);
    } catch (error) {
      logger.error('Failed to flush metrics', error);
      // Re-add metrics to buffer for retry
      this.metricsBuffer.unshift(...metrics);
    }
  }

  /**
   * Get system health status
   */
  getHealthStatus(): {
    status: 'healthy' | 'degraded' | 'unhealthy';
    checks: Record<string, boolean>;
    metrics: {
      errorRate: number;
      avgResponseTime: number;
      activeUsers: number;
    };
  } {
    try {
      if (import.meta.env.DEV) {
        console.log('📊 getHealthStatus called - using console.log');
        logger.info('📊 getHealthStatus called');
      }

      const checks: Record<string, boolean> = {
        database: true, // Assume true for now to avoid hanging
        storage: true,
        authentication: true,
        monitoring: this.initialized,
      };

      if (import.meta.env.DEV) {
        console.log('📊 Checks prepared:', checks);
        logger.info('📊 Checks prepared:', checks);
      }

      // Skip database check for now - it might be causing the hang
      // We'll check it differently later
      /*
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      await supabase.from('projects').select('count').limit(1);
      checks.database = true;
    } catch {
      checks.database = false;
    }
    */

      // Calculate health status
      const healthyChecks = Object.values(checks).filter(Boolean).length;
      const totalChecks = Object.values(checks).length;
      const healthPercentage = (healthyChecks / totalChecks) * 100;

      let status: 'healthy' | 'degraded' | 'unhealthy';
      if (healthPercentage >= 90) {
        status = 'healthy';
      } else if (healthPercentage >= 50) {
        status = 'degraded';
      } else {
        status = 'unhealthy';
      }

      // Calculate real metrics
      const errorRate = this.requestCount > 0 ? (this.errorCount / this.requestCount) * 100 : 0;

      const avgResponseTime =
        this.requestCount > 0 ? Math.round(this.totalResponseTime / this.requestCount) : 0;

      const result = {
        status,
        checks,
        metrics: {
          errorRate,
          avgResponseTime,
          activeUsers: this.activeUsers.size,
        },
      };

      logger.info('📊 Health status result:', result);
      return result;
    } catch (error) {
      logger.error('❌ Error in getHealthStatus:', error);
      throw error;
    }
  }

  /**
   * Cleanup monitoring
   */
  cleanup() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    if (this.userActivityInterval) {
      clearInterval(this.userActivityInterval);
      this.userActivityInterval = null;
    }
    this.flushMetrics();
    this.initialized = false;
  }
}

// Export singleton instance
export const monitoring = new MonitoringService();

// Export types
export type { PerformanceMetrics, BusinessMetrics, ErrorContext };
